import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Pill, Clock, CheckCircle2, XCircle, AlertTriangle, Plus, 
  RefreshCw, Calendar, Bell, History, ChevronRight, Info
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const MedicationReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reminders');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReminder, setNewReminder] = useState({
    medication_name: '',
    dose: '',
    schedule_times: [],
    instructions: '',
    priority: 'medium'
  });
  const { toast } = useToast();
  
  const token = localStorage.getItem('token');

  const fetchReminders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase46/medication-reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReminders(data.reminders || []);
      }
    } catch (err) {
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase46/medication-reminders/history?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchReminders();
    fetchHistory();
  }, [fetchReminders, fetchHistory]);

  const logMedication = async (reminder, action) => {
    try {
      const res = await fetch(`${API}/api/phase46/medication-reminders/${reminder.id}/log`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          medication_name: reminder.medication_name,
          notes: action === 'given' ? 'Administered as scheduled' : 'See notes'
        })
      });
      
      if (res.ok) {
        toast({
          title: action === 'given' ? "Medication Logged" : "Medication Skipped",
          description: `${reminder.medication_name} has been logged as ${action}`
        });
        fetchReminders();
        fetchHistory();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to log medication", variant: "destructive" });
    }
  };

  const addReminder = async () => {
    try {
      const res = await fetch(`${API}/api/phase46/medication-reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newReminder)
      });
      
      if (res.ok) {
        toast({ title: "Reminder Added", description: "New medication reminder created" });
        setShowAddForm(false);
        setNewReminder({
          medication_name: '',
          dose: '',
          schedule_times: [],
          instructions: '',
          priority: 'medium'
        });
        fetchReminders();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to add reminder", variant: "destructive" });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getTimeSince = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHrs > 0) return `${diffHrs}h ${diffMins}m ago`;
    return `${diffMins}m ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="medication-reminders">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Pill className="h-7 w-7 text-teal-600" />
            Medication Reminders
          </h1>
          <p className="text-gray-600">Track and log medication administration</p>
        </div>
        <Button 
          onClick={() => setShowAddForm(true)}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="add-reminder-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Reminder
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'reminders' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('reminders')}
          className={activeTab === 'reminders' ? 'bg-teal-600' : ''}
        >
          <Bell className="h-4 w-4 mr-2" />
          Today's Reminders
        </Button>
        <Button
          variant={activeTab === 'history' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('history')}
          className={activeTab === 'history' ? 'bg-teal-600' : ''}
        >
          <History className="h-4 w-4 mr-2" />
          History
        </Button>
      </div>

      {/* Add Reminder Form */}
      {showAddForm && (
        <Card className="border-teal-200 bg-teal-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Add New Medication Reminder</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Medication Name</label>
                <Input
                  value={newReminder.medication_name}
                  onChange={(e) => setNewReminder({ ...newReminder, medication_name: e.target.value })}
                  placeholder="e.g., Morphine Sulfate"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Dose</label>
                <Input
                  value={newReminder.dose}
                  onChange={(e) => setNewReminder({ ...newReminder, dose: e.target.value })}
                  placeholder="e.g., 15mg"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Instructions</label>
              <Textarea
                value={newReminder.instructions}
                onChange={(e) => setNewReminder({ ...newReminder, instructions: e.target.value })}
                placeholder="Special instructions..."
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={addReminder} className="bg-teal-600 hover:bg-teal-700">
                Save Reminder
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reminders List */}
      {activeTab === 'reminders' && (
        <div className="grid gap-4">
          {reminders.length > 0 ? (
            reminders.map((reminder) => (
              <Card 
                key={reminder.id} 
                className={`border-l-4 ${reminder.priority === 'high' ? 'border-l-red-500' : reminder.priority === 'medium' ? 'border-l-amber-500' : 'border-l-green-500'}`}
                data-testid={`reminder-${reminder.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{reminder.medication_name}</h3>
                        <Badge className={getPriorityColor(reminder.priority)}>
                          {reminder.priority}
                        </Badge>
                      </div>
                      <p className="text-gray-600 font-medium">{reminder.dose}</p>
                      <p className="text-sm text-gray-500 mt-1">{reminder.instructions}</p>
                      
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Next: {formatTime(reminder.next_due)}
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Last: {getTimeSince(reminder.last_given)}
                        </span>
                      </div>
                      
                      {reminder.caregiver_notes && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-500 mt-0.5" />
                          <span className="text-blue-700">{reminder.caregiver_notes}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => logMedication(reminder, 'given')}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`give-med-${reminder.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Given
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => logMedication(reminder, 'skipped')}
                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                        data-testid={`skip-med-${reminder.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Skip
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Pill className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">No Medication Reminders</h3>
                <p className="text-sm text-gray-500">Add reminders to track medication administration</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* History List */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle>Administration History (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.length > 0 ? (
                history.map((log, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {log.action === 'given' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-amber-500" />
                      )}
                      <div>
                        <p className="font-medium">{log.medication_name} {log.dose}</p>
                        <p className="text-sm text-gray-500">
                          {log.action === 'given' ? 'Administered' : 'Skipped'} by {log.given_by}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(log.given_at).toLocaleString()}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">No history available</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Tips */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Medication Safety Reminder</h4>
              <p className="text-sm text-amber-700">
                Always verify the medication, dose, and time before administration. 
                If you notice any concerning symptoms after giving medication, 
                contact the hospice nurse immediately at 703-578-7108.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MedicationReminders;
